/**
 * MinimumStockConfigModal
 * Configuração dos parâmetros de cálculo de estoque mínimo.
 */

import React, { useEffect, useState } from 'react'
import { X, Settings2, Loader2, AlertCircle } from 'lucide-react'
import { minimumStockService } from '../../../../services/purchasesApi'
import './MinimumStockConfigModal.css'

const COVERAGE_OPTIONS = [1, 2, 3, 4, 6, 9, 12]
const TREND_OPTIONS = [1, 2, 3, 4, 6]
const HISTORY_OPTIONS = [6, 12, 18, 24, 36]

function MinimumStockConfigModal({ isOpen, onClose, onSaved }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    coverageMonths: 3,
    trendMonths: 3,
    historyMonths: 24
  })

  useEffect(() => {
    if (!isOpen) return

    const loadConfig = async () => {
      setLoading(true)
      setError('')
      try {
        const response = await minimumStockService.getConfig()
        if (response.success && response.data) {
          setForm({
            coverageMonths: response.data.coverageMonths ?? 3,
            trendMonths: response.data.trendMonths ?? 3,
            historyMonths: response.data.historyMonths ?? 24
          })
        } else {
          setError(response.message || 'Não foi possível carregar a configuração')
        }
      } catch (err) {
        console.error('Erro ao carregar configuração:', err)
        setError(err.response?.data?.message || err.message || 'Erro ao carregar configuração')
      } finally {
        setLoading(false)
      }
    }

    loadConfig()
  }, [isOpen])

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      const response = await minimumStockService.updateConfig(form)
      if (response.success) {
        onSaved?.(response.data)
        onClose?.()
        return
      }
      setError(response.message || 'Não foi possível salvar a configuração')
    } catch (err) {
      console.error('Erro ao salvar configuração:', err)
      setError(err.response?.data?.message || err.message || 'Erro ao salvar configuração')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="minimum-stock-config-overlay" onClick={onClose}>
      <div className="minimum-stock-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="minimum-stock-config-header">
          <div className="minimum-stock-config-header-left">
            <div className="minimum-stock-config-icon">
              <Settings2 size={22} />
            </div>
            <div>
              <h3>Configurar cálculo</h3>
              <p>Parâmetros usados na análise de estoque mínimo por marca</p>
            </div>
          </div>
          <button type="button" className="minimum-stock-config-close" onClick={onClose} aria-label="Fechar">
            <X size={22} />
          </button>
        </div>

        {loading ? (
          <div className="minimum-stock-config-loading">
            <Loader2 size={24} className="spinning" />
            <span>Carregando configurações...</span>
          </div>
        ) : (
          <div className="minimum-stock-config-body">
            {error ? (
              <div className="minimum-stock-config-error" role="alert">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            ) : null}

            <div className="minimum-stock-config-field">
              <label htmlFor="coverage-months">Meses de cobertura</label>
              <p className="minimum-stock-config-hint">
                Quantidade de meses de venda média usada como base do estoque mínimo sugerido.
              </p>
              <select
                id="coverage-months"
                value={form.coverageMonths}
                onChange={(e) => setForm((prev) => ({ ...prev, coverageMonths: Number(e.target.value) }))}
              >
                {COVERAGE_OPTIONS.map((months) => (
                  <option key={months} value={months}>{months} {months === 1 ? 'mês' : 'meses'}</option>
                ))}
              </select>
            </div>

            <div className="minimum-stock-config-field">
              <label htmlFor="trend-months">Janela de tendência</label>
              <p className="minimum-stock-config-hint">
                Meses comparados para detectar crescimento ou queda nas vendas.
              </p>
              <select
                id="trend-months"
                value={form.trendMonths}
                onChange={(e) => setForm((prev) => ({ ...prev, trendMonths: Number(e.target.value) }))}
              >
                {TREND_OPTIONS.map((months) => (
                  <option key={months} value={months}>{months} {months === 1 ? 'mês' : 'meses'}</option>
                ))}
              </select>
            </div>

            <div className="minimum-stock-config-field">
              <label htmlFor="history-months">Histórico analisado</label>
              <p className="minimum-stock-config-hint">
                Período máximo de vendas considerado quando não houver filtro de datas.
              </p>
              <select
                id="history-months"
                value={form.historyMonths}
                onChange={(e) => setForm((prev) => ({ ...prev, historyMonths: Number(e.target.value) }))}
              >
                {HISTORY_OPTIONS.map((months) => (
                  <option key={months} value={months}>{months} meses</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="minimum-stock-config-footer">
          <button type="button" className="minimum-stock-config-btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="minimum-stock-config-btn-primary"
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? <Loader2 size={16} className="spinning" /> : null}
            Salvar configuração
          </button>
        </div>
      </div>
    </div>
  )
}

export default MinimumStockConfigModal
